import { API_PROXY_PREFIXES } from "@/constants/api-proxy.mjs";
import ApiHosts from "@/libs/ApiHosts";
import { isServer } from "@/libs/isServer";

/**
 * 서버·브라우저 양쪽에서 부를 수 있는 백엔드 — 서버가 직접 부를 호스트(`ApiHosts`)가 있는 것만이다.
 *
 * 브라우저 갈래의 프록시 접두사는 사본을 두지 않고 `next.config.mjs` `rewrites()` · 로케일 미들웨어와 같은
 * 단일 출처(`src/constants/api-proxy.mjs`)에서 읽는다. 그 접두사의 리라이트 목적지가 `ApiHosts` 의 같은
 * 키와 같다는 것은 `__tests__/libs/backend-url.test.ts` 가 설정을 실제로 읽어 잠근다.
 */
type BackendApi = keyof typeof ApiHosts;

// 같은 객체를 타입만 좁혀 받는다(값 사본 아님) — `ApiHosts` 에 키가 늘었는데 공유 목록에 접두사가 없으면
// 여기서 타입 에러가 난다. `noImplicitAny` 가 꺼져 있어 `API_PROXY_PREFIXES[api]` 인덱싱만으로는 안 잡힌다.
const PROXY_PREFIXES: Record<BackendApi, string> = API_PROXY_PREFIXES;

/**
 * 서버·브라우저 양쪽에서 부르는 백엔드 엔드포인트의 주소. 서버는 백엔드를 직접, 브라우저는
 * 리라이트 프록시로 부른다.
 *
 * ★**끝슬래시는 브라우저 쪽에만 붙는다.** 두 요청이 지나는 길이 달라서다(dev 실측 2026-10-01):
 *
 * ```
 * 브라우저  /sdk-api/api/v1/x     308 → /sdk-api/api/v1/x/   trailingSlash — 리라이트 전에 왕복 하나
 * 브라우저  /sdk-api/api/v1/x/    200   Next 가 끝슬래시를 떼고 넘긴다 — 백엔드는 /api/v1/x 를 받는다
 * 서버      <sdk>/api/v1/x        200
 * 서버      <sdk>/api/v1/x/       404   Spring 6 는 끝슬래시를 매칭하지 않는다 (mgmt 는 500)
 * ```
 *
 * 그래서 `isServer ? ApiHosts.x : "/x-api"` 로 고른 앞부분에 경로를 이어 붙이면, 끝슬래시를 어느
 * 쪽에 맞춰도 다른 쪽이 틀린다. `__tests__/ci/api-fetch-trailing-slash.test.ts` 가 그 형태를 잡는다.
 *
 * @param path `/api/…` — 끝슬래시와 쿼리 없이 (쿼리는 `query` 로)
 * @param query 비어 있지 않으면 `?` 뒤에 붙는다 (docs/pitfalls.md #64 「URL 쿼리에 값을 원문 그대로
 *   붙이지 않는다」)
 */
export function backendUrl(
  api: BackendApi,
  path: string,
  query?: URLSearchParams,
): string {
  const qs = query?.toString();
  const search = qs ? `?${qs}` : "";
  return isServer
    ? `${ApiHosts[api]}${path}${search}`
    : `${PROXY_PREFIXES[api]}${path}/${search}`;
}
